import React from "react";
import Button from "Components/DesignSystem/Button/Button";
import EmptyState from "Components/DesignSystem/EmptyState/EmptyState";
import NoDataIllustration from "Components/DesignSystem/EmptyState/NoDataIllustration";

/**
 * The connector log grid's empty state — no ingestion history for this feed yet.
 *
 * On the design system since § 131. Copy is unchanged to the character: the heading is
 * still `No data in {module}` lower-cased and the body still reads "You can click on
 * import data to configure and import {module} data".
 *
 * Two things the hand-rolled version got wrong, both visible in the screenshots that
 * prompted this migration:
 *
 * - **`tw-mr-[30%]` on the text block.** The illustration was centred and the copy was
 *   not, so the heading and the button sat noticeably left of the artwork above them.
 *   There is no comment or ticket explaining the 30%, and nothing else on the page
 *   lines up with it. `EmptyState` centres its stack, which is what the layout was
 *   reaching for.
 * - **A 24px heading in a hardcoded `#091caa`.** That hex is `accent-bold`; going
 *   through the token means it follows the palette instead of freezing one theme's
 *   value into this file.
 *
 * The illustration is now the design system's `NoDataIllustration` rather than a
 * `<img src={PUBLIC_URL + "/img/place_holder_no_data.svg"} alt="logo">` — an
 * `alt="logo"` on decorative artwork is a screen reader announcing "logo" in the
 * middle of an empty table (WCAG 1.1.1); `EmptyState` renders it `aria-hidden` and
 * lets the title carry the meaning.
 *
 * `module` can be undefined for one render — `ConnectionsHome` mounts the table before
 * `currentTab` is set — so both interpolations keep their optional chaining.
 */
function NoData(props) {
  const module = props?.module?.toLowerCase();

  return (
    <EmptyState
      title={`No data in ${module}`}
      description={`You can click on import data to configure and import ${module} data`}
      illustration={<NoDataIllustration />}
      data-testid="connectors-empty"
      action={
        <Button
          variant="primary"
          data-testid="connectors-empty-import-btn"
          disabled={!props?.canImportData}
          onClick={() => props?.setShowImportDataForm(true)}
        >
          Data Import
        </Button>
      }
    />
  );
}

export default NoData;
